"use client";

import React, { createContext, useContext } from "react";
import type { SavedView, SavedViewVisibility } from "@/lib/types/saved-views";
import type { UseMutationResult } from "@tanstack/react-query";

/**
 * Context for managing shared view modal state
 * Eliminates prop drilling for share/delete modals
 */
export interface SavedViewsModalContextValue {
  // Current view being edited
  selectedView: SavedView | undefined;
  selectedViewId: string | undefined;
  resource: string;
  entityName?: string;

  // Mutations
  createViewMutation: UseMutationResult<SavedView, Error, any>;
  updateViewMutation: UseMutationResult<SavedView, Error, any>;
  deleteViewMutation: UseMutationResult<{ id: string; resource: string }, Error, any>;

  // Filter helpers
  serializeFilters: (() => any) | null;

  // Save As modal state
  saveAsName?: string;
  setSaveAsName?: (name: string) => void;

  // Share modal state
  shareOpen: boolean;
  setShareOpen: (open: boolean) => void;
  shareVisibility: SavedViewVisibility;
  setShareVisibility: (visibility: SavedViewVisibility) => void;
  shareUserIds: string[];
  setShareUserIds: (ids: string[] | ((prev: string[]) => string[])) => void;

  // Delete modal state
  deleteOpen: boolean;
  setDeleteOpen: (open: boolean) => void;

  // Handlers
  onShare: () => void;
  onDelete: () => void;
}

const SavedViewsModalContext = createContext<SavedViewsModalContextValue | undefined>(undefined);

export function useSavedViewsModal() {
  const context = useContext(SavedViewsModalContext);
  if (!context) {
    throw new Error("useSavedViewsModal must be used within SavedViewsModalProvider");
  }
  return context;
}

interface SavedViewsModalProviderProps {
  value: SavedViewsModalContextValue;
  children: React.ReactNode;
}

export function SavedViewsModalProvider({ value, children }: SavedViewsModalProviderProps) {
  return (
    <SavedViewsModalContext.Provider value={value}>
      {children}
    </SavedViewsModalContext.Provider>
  );
}
